import React, { useCallback, useContext, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { ErrorOption, FieldPath, FieldValues, FormProvider, useForm } from 'react-hook-form';
import Loading from 'react-loading';
import LocalizationContext from '../../context/LocalizationContext';
import PassOnTicketsModal from '../account/myTicketsAndPasses/MyWaitlists/WaitlistAccordionItem/PassOnTicketsModal';
import './RegistrationForm.css';
import { useMyTicketsContext } from '../../context/MyTicketsContext';
import ConfirmModal from '../tickets/signup/guests/modal/ConfirmModal';
import { NintendoAccountFamilyUser, Party, PartyMember } from '../../utils/types';

interface RegistrationFormProps extends React.PropsWithChildren<{}> {
    title: string | JSX.Element | null;
    onSubmit?: (
        data: FieldValues,
        setError?: (
            name: FieldPath<FieldValues>,
            error: ErrorOption,
            options?: { shouldFocus: boolean },
        ) => void,
    ) => void | Promise<void>;
    buttonLabel?: string;
    isWaitlist?: boolean;
    onPassWaitlist?: () => void | Promise<void>;
    selectedGuests: NintendoAccountFamilyUser[];
    isDrawing: boolean;
    isActivity?: boolean;
    party?: Party | null;
    maxAttendees?: number;
}

/**
 * Form component for various registration processes. Since the various registration components (e.g. TicketSignUp)
 * can get rather larger, this component is to help clean those components up and to standardize on around single
 * FormProvider.
 *
 * @param props
 * @param props.title - title
 * @param props.onSubmit - form submission callback method with the following params:
 *      data: passes a FieldValues object with all the fields data
 *      setError: optional react-hook-form setError hook, so you can add custom error messages based on responses
 * @param props.buttonLabel - optional string label for the button. Defaults to "Confirm"
 * @param props.isWaitlist - Optional boolean indicating if waitlist is enabled
 * @param props.onPassWaitlist - Callback function triggered when a guest passes the waitlist.
 * @param props.selectedGuests - Array of selected guests to display in the modal
 * @param props.isDrawing - Boolean indicating if this is for a drawing
 * @param props.isActivity - Boolean indicating if this is for activity
 * @param props.party - Optional Party object containing event gate attendees used to determine what guests to display
 *   in the ConfirmationModal
 * @param props.maxAttendees - optional Max Attendees per Entry
 * @param props.children - list of child components
 */
const RegistrationForm = ({
    title,
    onSubmit,
    buttonLabel,
    isWaitlist,
    onPassWaitlist,
    selectedGuests,
    isDrawing,
    isActivity,
    party,
    maxAttendees = 1,
    children,
}: RegistrationFormProps) => {
    const localizedStrings = useContext(LocalizationContext);
    const { refresh: refreshMyTickets } = useMyTicketsContext();
    const formMethods = useForm({ mode: 'onChange', shouldUnregister: true });

    const { setError } = formMethods;
    const { isSubmitting, isValidating } = formMethods.formState;

    const [isSubmittingToBackend, setIsSubmittingToBackend] = useState(false);

    // Make sure the button is disabled while we are submitting the data...
    const onFormSubmit = async (values: FieldValues) => {
        setIsSubmittingToBackend(true);
        if (onSubmit) {
            await onSubmit(values, setError);
        }
        setIsSubmittingToBackend(false);
    };

    const [showPassModal, setShowPassModal] = useState(false);
    const openPassModal = useCallback(() => setShowPassModal(true), []);
    const closePassModal = useCallback(() => setShowPassModal(false), []);
    const [isPassingWaitlistTicket, setIsPassingWaitlistTicket] = useState(false);

    const handlePassWaitlist = useCallback(async () => {
        if (isPassingWaitlistTicket) {
            return;
        }
        setIsPassingWaitlistTicket(true);
        try {
            await onPassWaitlist?.();
            refreshMyTickets();
            closePassModal();
        } finally {
            setIsPassingWaitlistTicket(false);
        }
    }, [closePassModal, isPassingWaitlistTicket, onPassWaitlist]);

    const [isConfirmModalVisible, setConfirmModalVisible] = useState(false);
    // Watch selected attendees, get selectedIds, and create a list of PartyMember for selected participant IDs.
    const attendees = formMethods.watch('attendees', {});
    const selectedIds = Object.keys(attendees).filter((id) => attendees[id]);
    const selectedEventGateGuests: PartyMember[] =
        party?.members.filter((member) =>
            selectedIds.includes(member.participant_identification.value),
        ) ?? [];

    const guestsToPass = !(selectedGuests?.length && selectedGuests.length > 0)
        ? selectedEventGateGuests ?? []
        : selectedGuests;

    const handleButtonClick = () => {
        if (
            !(selectedGuests.length > 0 || selectedEventGateGuests.length > 0 || maxAttendees > 1)
        ) {
            formMethods.handleSubmit(onFormSubmit)();
        } else {
            setConfirmModalVisible(true);
        }
    };

    return (
        <div data-testid="reservation-container">
            {React.isValidElement(title) ? title : <h2 className="mt-0">{title}</h2>}
            <FormProvider {...formMethods}>
                <form
                    onSubmit={formMethods.handleSubmit(async (values) => {
                        await onFormSubmit(values);
                    })}
                >
                    <div className="sr-only">
                        {localizedStrings.tickets.registration.registrant.requiredFields}
                    </div>

                    {children}

                    {isWaitlist ? (
                        <Row>
                            <Col xs={6} sm={8} md={9} />
                            <Col
                                xs={6}
                                sm={4}
                                md={3}
                                className="registration-form-waitlist-buttons-container"
                            >
                                <Button
                                    variant="outline-danger"
                                    disabled={
                                        isPassingWaitlistTicket ||
                                        isSubmitting ||
                                        isValidating ||
                                        isSubmittingToBackend
                                    }
                                    onClick={openPassModal}
                                >
                                    {isPassingWaitlistTicket ? (
                                        <Loading type="spin" height={25} width={25} />
                                    ) : (
                                        localizedStrings.tickets.waitlist.passOnTickets
                                    )}
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={
                                        isPassingWaitlistTicket ||
                                        isSubmitting ||
                                        isValidating ||
                                        isSubmittingToBackend
                                    }
                                >
                                    {isSubmitting || isSubmittingToBackend ? (
                                        <Loading type="spin" height={25} width={25} />
                                    ) : (
                                        localizedStrings.tickets.waitlist.claimTickets
                                    )}
                                </Button>
                                <PassOnTicketsModal
                                    show={showPassModal}
                                    onHide={closePassModal}
                                    onConfirm={handlePassWaitlist}
                                    submitting={isPassingWaitlistTicket}
                                />
                            </Col>
                        </Row>
                    ) : (
                        <div>
                            <div className="row my-5 mx-0">
                                <Button
                                    variant="primary"
                                    onClick={handleButtonClick}
                                    className="w-100 d-flex justify-content-center align-items-center"
                                    disabled={isSubmitting || isValidating || isSubmittingToBackend}
                                    data-testid="ticket-signup-submit-button"
                                >
                                    {isSubmitting || isValidating || isSubmittingToBackend ? (
                                        <Loading type="spin" height={20} width={20} />
                                    ) : (
                                        buttonLabel || localizedStrings.general.confirm
                                    )}
                                </Button>
                            </div>

                            {isConfirmModalVisible && (
                                <ConfirmModal
                                    selectedGuests={guestsToPass}
                                    isDrawing={isDrawing}
                                    isActivity={isActivity}
                                    isEventGate={!!party}
                                    onClose={() => setConfirmModalVisible(false)}
                                    onConfirm={() => {
                                        setConfirmModalVisible(false);
                                        formMethods.handleSubmit(onFormSubmit)();
                                    }}
                                    maxAttendees={maxAttendees}
                                />
                            )}
                        </div>
                    )}
                </form>
            </FormProvider>
        </div>
    );
};

export default RegistrationForm;
